import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import GoogleLoginButton from '../components/GoogleLoginButton';
import AppleLoginButton from '../components/AppleLoginButton';
import { MonoLabel } from '../components/Editorial';
import Button from '../components/Button';
import { sanitizeNext } from '../lib/publicProfile';
import { canAccessAthleteDetail } from '../lib/athleteDetail';
import { clientProofMessage, markProofStale, prepareClientProof } from '../lib/clientProof';

export default function LoginPage() {
  const { login, API_URL } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Run the Turnstile check while the user types, not after they click Sign in
  // (it took 1.3–3.9 s on every web login in prod, all behind the spinner).
  // Re-run on focus in case a prepared proof went stale on an idle page.
  const prepareProof = () => prepareClientProof(API_URL, 'login');
  useEffect(() => { prepareClientProof(API_URL, 'login'); }, [API_URL]);

  useEffect(() => {
    const appleToken = searchParams.get('apple_token');
    if (appleToken) {
      localStorage.setItem('token', appleToken);
      markProofStale();
      axios.defaults.headers.common['Authorization'] = `Bearer ${appleToken}`;
      window.location.href = '/dashboard';
    }
    const errParam = searchParams.get('error');
    if (errParam === 'existing_account_use_invite') {
      setError("You're already registered — we just re-sent your ProZpects invite link to your email. Check your inbox (and spam) to finish setting up your account.");
    } else if (errParam && errParam.startsWith('apple_')) {
      setError('Apple sign-in failed. Please try again.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const signedIn = await login(email, password);
      // `next` is only ever set by a coach-gated CTA (today, the public athlete
      // profile), and every destination behind it is staff-only. An athlete or
      // guardian who follows one would land on a 403, so they go to their own
      // dashboard instead.
      const next = sanitizeNext(searchParams.get('next'));
      const role = signedIn && signedIn.role;
      navigate(next && canAccessAthleteDetail(role) ? next : '/dashboard');
    } catch (err) {
      setError(clientProofMessage(err) || err.response?.data?.detail || 'Login failed');
      prepareProof(); // the failed attempt used up the prepared proof; ready the retry
    }
    setLoading(false);
  };

  // A `next` crossing a page boundary has no provenance — it is re-read from
  // this page's own URL, which anyone can compose, so it is re-validated here
  // rather than trusted just because it came from the previous hop. Forwarded
  // unsanitized, an attacker-crafted next would survive the redirect through
  // signup looking legitimate to whatever eventually navigate()s it.
  const signupNext = sanitizeNext(searchParams.get('next'));

  return (
    <div className="ed-auth">
      <div className="ed-auth__hero">
        <div>
          <MonoLabel onDark>PROZPECTS · RECRUITING</MonoLabel>
          <h1 className="ed-auth__hero-headline" style={{ marginTop: 16 }}>
            Verified data.<br /><em>Coach reach.</em>
          </h1>
        </div>
        <p className="ed-auth__hero-quote">
          "Coaches are 73% more likely to make an offer to athletes with verified data."
        </p>
      </div>

      <div className="ed-auth__form-wrap">
        <div className="ed-auth__form">
          <div className="auth-logo" style={{ marginBottom: 24 }}>
            <img src="/logo-wordmark.png" alt="ProZpects" style={{ maxHeight: 36 }} />
          </div>
          <MonoLabel>WELCOME BACK</MonoLabel>
          <h2 style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 36, fontWeight: 400, letterSpacing: '-0.015em', margin: '8px 0 24px' }}>
            Sign in.
          </h2>

          <AppleLoginButton />
          <GoogleLoginButton />

          <div className="auth-divider"><span>or</span></div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {error && <div className="auth-error" role="alert">{error}</div>}

            <div className="auth-field">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={prepareProof}
                required
              />
            </div>

            <div className="auth-field">
              <label htmlFor="login-password">Password</label>
              <div className="auth-password-wrapper">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={prepareProof}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <Link to="/forgot-password" className="auth-footer a" style={{ fontSize: '14px' }}>
                Forgot password?
              </Link>
            </div>

            <Button type="submit" variant="primary" className="auth-btn" disabled={loading}>
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </form>

          <div className="auth-footer">
            Don't have an account? <Link to={`/register${signupNext ? `?next=${encodeURIComponent(signupNext)}` : ''}`}>Sign up</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
